import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../services/authWrapper";
import EnterOtpModal from "./enterOtpModal";
import Logo from "../../assets/img/skorLogo.svg";
import UserIcon from "../../assets/img/user-icon-gray.svg";
import useTranslator from "../../hooks/useTranslator";
import { toast } from "../../helper/common";
import { PencilEditIcon } from "../../helper/svgHelper";
import "./applicationProfiles.css";

type Profile = {
  name: string;
  email: string;
  is_lead: boolean;
  profile_exists: boolean;
  requires_login: boolean;
  onboarding_status: "COMPLETED" | "PENDING" | "OUTDATED";
  require_update?: boolean;
  require_update_note?: string;
};

const ApplicationProfiles = () => {
  const { _t } = useTranslator();
  const translatorRef = useRef(_t);
  translatorRef.current = _t;
  const [profiles, setProfiles] = useState<Profile[]>([]);
  console.log("profiles", profiles);
  const [loading, setLoading] = useState(true);
  const [workingEmail, setWorkingEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationName, setVerificationName] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showResend, setShowResend] = useState(true);
  const [showTimer, setShowTimer] = useState(60);
  const [submitted, setSubmitted] = useState(false);

  const serverBase = (process.env.REACT_APP_SERVER_URL || "").replace(
    /\/$/,
    ""
  );
  const apiBase = serverBase.endsWith("/api/v1")
    ? serverBase
    : `${serverBase}/api/v1`;
  const managerToken = localStorage.getItem("token") || "";

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/application-profiles`,
        { token: managerToken }
      );
      setProfiles(data.data?.profiles || []);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          translatorRef.current("unableToContinue")
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, managerToken]);

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (!showOtp || showResend) return;
    const timer = window.setInterval(() => {
      setShowTimer((seconds) => {
        if (seconds > 1) return seconds - 1;
        window.clearInterval(timer);
        setShowResend(true);
        return 60;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [showOtp, showResend]);

  const completedCount = useMemo(
    () =>
      profiles.filter(
        ({ onboarding_status }) => onboarding_status === "COMPLETED"
      ).length,
    [profiles]
  );

  const hasPendingUpdates = useMemo(
    () => profiles.some((profile) => Boolean(profile.require_update)),
    [profiles]
  );

  const launchOnboarding = useCallback(
    (token: string, profile?: Profile) => {
      sessionStorage.setItem("applicationProfileManagerToken", managerToken);
      sessionStorage.setItem("applicationProfiles", JSON.stringify(profiles));
      if (profile) {
        sessionStorage.setItem("activeApplicationProfileEmail", profile.email);
      }
      sessionStorage.removeItem("applicationProfileEditMode");
      localStorage.setItem("token", token);
      localStorage.setItem("application_profile_child", "true");
      const query = new URLSearchParams({
        token,
        usertype: "tenant",
        is_verified: "true",
        app_type: "skor-sdk",
        go_to_page: "/onboarding?welcome=true",
      });
      window.location.assign(`/redirect?${query.toString()}`);
    },
    [managerToken, profiles]
  );

  const launchEditOnboarding = useCallback(
    (token: string, profile?: Profile) => {
      sessionStorage.setItem("applicationProfileManagerToken", managerToken);
      sessionStorage.setItem("applicationProfiles", JSON.stringify(profiles));
      if (profile) {
        sessionStorage.setItem("activeApplicationProfileEmail", profile.email);
      }
      sessionStorage.setItem("applicationProfileEditMode", "true");
      localStorage.setItem("token", token);
      localStorage.setItem("application_profile_child", "true");
      const query = new URLSearchParams({
        token,
        usertype: "tenant",
        is_verified: "true",
        app_type: "skor-sdk",
        go_to_page: "/onboarding?welcome=true&editProfile=true",
      });
      window.location.assign(`/redirect?${query.toString()}`);
    },
    [managerToken, profiles]
  );

  const startProfile = async (profile: Profile, isEdit = false) => {
    setWorkingEmail(profile.email);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/application-profiles/start`,
        {
          token: managerToken,
          tenant_email: profile.email,
          ...(isEdit ? { is_edit: true } : {}),
        }
      );
      if (data.data?.completed) {
        if (isEdit && data.data?.onboarding_token) {
          launchEditOnboarding(data.data.onboarding_token, profile);
          return;
        }
        if (isEdit) {
          toast.error(_t("unableToContinue"));
          return;
        }
        await loadProfiles();
        return;
      }
      if (data.data?.requires_verification) {
        setVerificationToken(data.data.verification_token);
        setVerificationEmail(profile.email);
        setVerificationName(profile.name);
        setOtp("");
        setShowOtp(false);
        return;
      }
      if (data.data?.onboarding_token) {
        if (isEdit) {
          launchEditOnboarding(data.data.onboarding_token, profile);
        } else {
          launchOnboarding(data.data.onboarding_token, profile);
        }
      }
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("unableToContinue")
      );
    } finally {
      setWorkingEmail("");
    }
  };

  const resendOtp = async () => {
    try {
      await axiosInstance.post(`${apiBase}/tenants/existing-profile/send-otp`, {
        token: verificationToken,
      });
      setShowTimer(60);
      setShowResend(false);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("failedToSendOtp")
      );
    }
  };

  const sendVerificationCode = async () => {
    if (!verificationToken || otpLoading) return;
    setOtpLoading(true);
    try {
      await axiosInstance.post(`${apiBase}/tenants/existing-profile/send-otp`, {
        token: verificationToken,
      });
      setOtp("");
      setShowTimer(60);
      setShowResend(false);
      setShowOtp(true);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message || _t("failedToSendOtp")
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (event?: any) => {
    event?.preventDefault?.();
    if (otp.length !== 6 || otpLoading) return;
    setOtpLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${apiBase}/tenants/existing-profile/verify-otp`,
        { token: verificationToken, otp }
      );
      setVerificationToken("");
      if (data.data?.requires_onboarding && data.data?.onboarding_token) {
        launchOnboarding(
          data.data.onboarding_token,
          profiles.find(({ email }) => email === verificationEmail)
        );
        return;
      }
      if (data.data?.is_profile_outdated && data.data?.renewal_token) {
        const renewal = await axiosInstance.post(
          `${apiBase}/tenants/existing-profile/reinitiate-onboarding`,
          { renewal_token: data.data.renewal_token }
        );
        launchOnboarding(
          renewal.data.data.token,
          profiles.find(({ email }) => email === verificationEmail)
        );
        return;
      }
      await loadProfiles();
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.message ||
          _t("OTPDoesnotMatchesPleasetryagain")
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const submitApplication = () => {
    setSubmitted(true);
    window.setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("application_profile_child");
      sessionStorage.removeItem("applicationProfileManagerToken");
      sessionStorage.removeItem("applicationProfiles");
      sessionStorage.removeItem("activeApplicationProfileEmail");
      window.parent.postMessage(
        {
          type: "TENANT_ONBOARDING_COMPLETED",
          data: { application_completed: true },
        },
        "*"
      );
    }, 1000);
  };

  if (submitted) {
    return (
      <main className="application-profiles-page">
        <section className="application-profiles-card application-profiles-submitted">
          <span className="application-profiles-check">✓</span>
          <h1>{_t("applicationSubmitted")}</h1>
        </section>
      </main>
    );
  }

  return (
    <main className="application-profiles-page">
      <section className="application-profiles-card">
        <h1>{_t("applicationProfiles")}</h1>
        <p>
          {completedCount} {_t("of")} {profiles.length}{" "}
          {_t("profilesCompletedDescription")}
        </p>
        <div className="application-profiles-help">
          <strong>{_t("haveTheseHandy")}</strong>
          <span>
            <img src="/images/idcard-icon.svg" alt="" />
            {_t("idOrPassport")}
          </span>
          <span>
            <img src="/images/briefcaseIcon.svg" alt="" />
            {_t("proofOfWorkAndIncome")}
          </span>
          <small>{_t("neededForEachTenant")}</small>
        </div>
        <div className="application-profiles-list">
          {profiles.map((profile) => {
            const completed = profile.onboarding_status === "COMPLETED";
            const outdated = profile.onboarding_status === "OUTDATED";
            const requiresUpdate = Boolean(profile.require_update);
            const statusNote = profile.require_update_note || "";
            return (
              <div
                className={`application-profile-row${requiresUpdate ? " is-changes-requested" : ""}`}
                key={profile.email}
              >
                <span className="application-profile-avatar">
                  <img src={UserIcon} alt="" />
                </span>
                <span className="application-profile-meta">
                  <strong>{profile.is_lead ? _t("you") : profile.name}</strong>
                  <small className={requiresUpdate ? "is-update-note" : ""}>
                    {requiresUpdate
                      ? statusNote ||
                        _t("changesRequestedNote", "Changes requested")
                      : completed
                        ? _t("profileCompleted")
                        : profile.requires_login
                          ? _t("loginToLinkProfile")
                          : outdated
                            ? _t("profileOutdated")
                            : _t("notStarted")}
                  </small>
                </span>
                {requiresUpdate || completed ? (
                  <span className="application-profile-actions">
                    <span
                      className={`application-profile-status ${requiresUpdate ? "is-warning" : "is-completed"}`}
                    >
                      <span
                        className="application-profile-status-icon"
                        aria-hidden="true"
                      >
                        {requiresUpdate ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M8 3.5L13.2 11.2C13.6 11.9 13.1 12.8 12.2 12.8H3.8C2.9 12.8 2.4 11.9 2.8 11.2L8 3.5Z"
                              fill="#F5B800"
                              stroke="#B78A00"
                              strokeWidth="1.1"
                            />
                            <path
                              d="M8 7V9.2"
                              stroke="white"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                            />
                            <circle cx="8" cy="11" r="1" fill="white" />
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <circle cx="8" cy="8" r="7" fill="#22C55E" />
                            <path
                              d="M5 8.2L7 10.2L11.2 5.8"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      {requiresUpdate
                        ? _t("changesRequested", "Changes requested")
                        : _t("completed")}
                    </span>
                    <button
                      type="button"
                      className="application-profile-edit-icon"
                      disabled={workingEmail === profile.email || loading}
                      onClick={() => {
                        if (requiresUpdate) {
                          // #NOTE: we are setting the step change in the backend
                          void startProfile(profile);
                        } else {
                          void startProfile(profile, true);
                        }
                      }}
                      aria-label={`${_t("edit", "Edit")} ${profile.name}`}
                    >
                      <PencilEditIcon color="#6b7280" size={16} />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={profile.requires_login ? "is-login" : ""}
                    disabled={workingEmail === profile.email || loading}
                    onClick={() => void startProfile(profile)}
                  >
                    {profile.requires_login && (
                      <img src="/images/skor-hand-logo.svg" alt="" />
                    )}
                    <span className="application-profile-action-label">
                      {profile.requires_login
                        ? _t("loginWithSkor")
                        : outdated
                          ? _t("profileIsOutdatedRedoOnboarding")
                          : _t("continueManually")}
                    </span>
                    <span
                      className="application-profile-action-chevron"
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button
          className="application-profiles-submit"
          disabled={!profiles.length || completedCount !== profiles.length}
          onClick={submitApplication}
        >
          {_t("sendApplication")}
        </button>
      </section>
      {verificationToken && (
        <>
          {!showOtp && (
            <div className="application-profile-verification-overlay">
              <section className="application-profile-verification-card">
                <header>
                  <img src={Logo} alt="SKOR" />
                </header>
                <small>
                  {_t("forTenant")} {verificationName}
                </small>
                <h2>{_t("tenantAlreadyHasSkorProfile")}</h2>
                <p>
                  {_t("sendVerificationCodeDescription")} {verificationEmail}
                </p>
                <button
                  type="button"
                  className="btnNext"
                  disabled={otpLoading}
                  onClick={() => void sendVerificationCode()}
                >
                  {otpLoading ? _t("sendingCode") : _t("sendVerificationCode")}
                </button>
                <button
                  type="button"
                  className="application-profile-verification-cancel"
                  onClick={() => {
                    setVerificationToken("");
                    setVerificationEmail("");
                    setVerificationName("");
                  }}
                >
                  {_t("cancel")}
                </button>
              </section>
            </div>
          )}
          {showOtp && (
            <EnterOtpModal
              otp={otp}
              setOtp={setOtp}
              onClose={() => {
                setVerificationToken("");
                setVerificationEmail("");
                setVerificationName("");
                setShowOtp(false);
              }}
              showResend={showResend}
              showTimer={showTimer}
              sendOtpFun={resendOtp}
              verifyOTPHandler={verifyOtp}
              verifyOtpLoading={otpLoading}
              email={verificationEmail}
            />
          )}
        </>
      )}
    </main>
  );
};

export default ApplicationProfiles;
